// Business tab endpoints (CHA1-1967).
// Contract: CHA1-1336_FE_INTEGRATION_GUIDE.md §5-§9.

import { request } from "../../../../Services/Api"

const BUSINESS_URL = "/consumer-clarity/configure/business"
// Status lives on the records viewset, not this tab's — it is the one manual
// status entrypoint shared with the admin Requests table (CHA1-1374).
const RECORDS_URL = "/consumer-clarity/records"

// Every endpoint here resolves its BA the same way: support admin is the only
// caller whose `?business_account_id` is read, and a merchant's session BA wins
// regardless of what is sent (chargeback_back consumer_clarity/views/business.py).
// So passing it when we have it is safe on both sides, and it is what lets the
// admin Configure page in Business settings render another merchant's data.
const withBusinessAccount = (params, businessAccountId) =>
  businessAccountId ? { ...params, business_account_id: businessAccountId } : params

// §5. Two program cards - always CC first, then OI.
export const fetchPrograms = (businessAccountId) =>
  request.api.get(`${BUSINESS_URL}/programs/`, withBusinessAccount({}, businessAccountId))

// §6. Paginated descriptor list per program. `status` may be a string or array
// (guide §6.1 accepts repeated `?status=` params or a comma-separated list).
export const fetchDescriptors = ({ program, page, status, search, businessAccountId }) => {
  const params = { program }
  if (page) params.page = page
  if (search) params.search = search
  if (Array.isArray(status) && status.length) params.status = status.join(",")
  else if (status && status !== "all") params.status = status
  return request.api.get(
    `${BUSINESS_URL}/descriptors/`,
    withBusinessAccount(params, businessAccountId)
  )
}

// §8. Add descriptors. CC takes descriptors only; OI requires one of
// {ARN} or {BIN+CAID} per submission (§8.2). The BA goes on the query string
// even here - the view reads it from `query_params`, not the body.
export const postActivation = (payload, businessAccountId) =>
  request.api.post(`${BUSINESS_URL}/activation/`, payload, {
    params: withBusinessAccount({}, businessAccountId)
  })

// §9. Full history for one record - `record_id`, NOT `billing_descriptor_id`
// (§6.4). 404 is deliberately generic on any lookup failure (§3).
export const fetchStatusLogs = (recordId, businessAccountId) =>
  request.api.get(
    `${BUSINESS_URL}/descriptors/${recordId}/status-logs/`,
    withBusinessAccount({}, businessAccountId)
  )

// Support-only Match type (CHA1-1975 BLOCK 3.5). `matchType` is "exact" or
// "starts_with"; the value is per descriptor, shared by both programs. Admin-only
// server-side, so this is never called from a merchant's own Configure.
export const postMatchType = ({ recordId, matchType, businessAccountId }) =>
  request.api.post(
    `${BUSINESS_URL}/descriptors/${recordId}/match-type/`,
    { match_type: matchType },
    { params: withBusinessAccount({}, businessAccountId) }
  )

// Support-only Status (CHA1-1975 BLOCK 3.4). Scoped by the caller's admin role
// rather than by business account, so it deliberately takes no BA parameter.
// Behaviour behind the change — transitions, provider triggers, notifications —
// is CHA1-1374's.
export const postDescriptorStatus = ({ recordId, status }) =>
  request.api.post(`${RECORDS_URL}/${recordId}/status/`, { status })

// Support-only Order Insight codes view (CHA1-1975 BLOCK 5). `codeType` is "arn"
// or "bin_caid". The response also carries `available_code_types`, already in
// cycle order and listing only what this BA actually has — that is what decides
// whether the toggle is a 2- or 3-state cycle.
export const fetchCodes = ({ codeType, search, businessAccountId }) =>
  request.api.get(
    `${BUSINESS_URL}/codes/`,
    withBusinessAccount({ code_type: codeType, search: search || "" }, businessAccountId)
  )
